import { useId, useState, type FormEvent } from "react";
import { UserPlus, Copy, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { useCreateUser } from "@/api/hooks";
import type { CreateUserResponse } from "@/api/types";
export function CreateUserButton() {
  const createUser = useCreateUser();
  const [open, setOpen] = useState(false);
  const [userId, setUserId] = useState("");
  const userIdInputId = useId();
  const [credentials, setCredentials] = useState<CreateUserResponse | null>(
    null
  );
  const [copied, setCopied] = useState<"username" | "password" | null>(null);

  function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (createUser.isPending) return;
    const username = userId.trim();
    createUser.mutate(username ? { username } : {}, {
      onSuccess: (data) => {
        setCredentials(data);
        setOpen(false);
        setUserId("");
      },
    });
  }

  function handleOpenChange(nextOpen: boolean) {
    if (createUser.isPending) return;
    setOpen(nextOpen);
    setUserId("");
    createUser.reset();
  }

  function handleCopy(field: "username" | "password", value: string) {
    navigator.clipboard.writeText(value);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
  }

  function handleDismiss() {
    setCredentials(null);
    createUser.reset();
  }

  return (
    <>
      <Button onClick={() => handleOpenChange(true)} disabled={createUser.isPending}>
        <UserPlus className="size-4" />
        {createUser.isPending ? "Creating..." : "Create User"}
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent onClose={() => handleOpenChange(false)}>
          <DialogHeader>
            <DialogTitle>Create User</DialogTitle>
            <DialogDescription>
              Specify a user ID for participant login, or leave it blank to generate a random six-digit ID.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate}>
            <label htmlFor={userIdInputId} className="mb-2 block text-sm font-medium">
              User ID (optional)
            </label>
            <Input
              id={userIdInputId}
              value={userId}
              onChange={(event) => {
                setUserId(event.target.value);
                createUser.reset();
              }}
              placeholder="e.g. study-001"
              maxLength={128}
              pattern={"\\s*(?!\\.{1,2}\\s*$)[A-Za-z0-9_.\\-]{1,128}\\s*|\\s*"}
              title="Use letters, numbers, periods, underscores, or hyphens (up to 128 characters). The ID cannot be . or .."
              aria-describedby={`${userIdInputId}-help`}
              autoComplete="off"
              autoFocus
              disabled={createUser.isPending}
            />
            <p id={`${userIdInputId}-help`} className="mt-2 text-xs text-muted-foreground">
              Up to 128 characters: letters, numbers, periods, underscores, or hyphens. Cannot be . or ..
            </p>
            {createUser.isError && (
              <p role="alert" className="mt-3 text-sm text-red-600">
                Failed to create user: {createUser.error.message}
              </p>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={createUser.isPending}>
                Cancel
              </Button>
              <Button type="submit" disabled={createUser.isPending}>
                {createUser.isPending ? "Creating..." : "Create User"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {credentials && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={handleDismiss}
        >
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md">
            <CredentialsCard credentials={credentials} copied={copied} onCopy={handleCopy} onDismiss={handleDismiss} />
          </div>
        </div>
      )}
    </>
  );
}

function CredentialsCard({
  credentials,
  copied,
  onCopy,
  onDismiss,
}: {
  credentials: CreateUserResponse;
  copied: "username" | "password" | null;
  onCopy: (field: "username" | "password", value: string) => void;
  onDismiss: () => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>New User Created</span>
          <Button variant="ghost" size="icon" onClick={onDismiss}>
            <X className="size-4" />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="mb-4 text-sm text-muted-foreground">
          Share these credentials with the participant. They will not be shown
          again.
        </p>
        <div className="space-y-3">
          {(["username", "password"] as const).map((field) => (
            <div
              key={field}
              className="flex items-center justify-between rounded-md border bg-muted/50 px-4 py-2.5"
            >
              <div className="min-w-0">
                <div className="text-xs text-muted-foreground capitalize">
                  {field}
                </div>
                <div className="break-all font-mono text-sm font-medium">
                  {credentials[field]}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="shrink-0"
                onClick={() => onCopy(field, credentials[field])}
              >
                {copied === field ? (
                  <Check className="size-4 text-green-600" />
                ) : (
                  <Copy className="size-4" />
                )}
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
